import { useState, useCallback, useEffect, useRef } from 'react';

interface PageResult {
  page: string;
  title: string;
}

export const useKeyboardNavigation = (
  items: PageResult[],
  onSelect: (page: string) => void,
  onClose: () => void,
  onSearch: (query: string) => void,
) => {
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  const onSearchRef = useRef(onSearch);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, value: string) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, -1));
          break;
        case 'Enter':
          event.preventDefault();
          if (selectedIndex >= 0 && selectedIndex < items.length) {
            onSelectRef.current(items[selectedIndex].page);
          } else if (value.trim()) {
            onSearchRef.current(value.trim());
          }
          break;
        case 'Escape':
          onCloseRef.current();
          break;
      }
    },
    [selectedIndex, items],
  ); // Only depend on selectedIndex and items

  // Reset selection when items change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [items]);

  return { selectedIndex, handleKeyDown };
};
